import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { RefreshCw, AlertTriangle, InfoIcon } from "lucide-react";

import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

import { ContractType, Currency } from "@/lib/types";
import { ContractFormValues } from "../ContractFormSchema";
import { useCurrencies } from "@/hooks/useCurrencies";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { currencyConverter } from "@/services/currencyConverter";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { FeatureGuard } from "@/components/common/FeatureGuard";

interface CostCurrencySectionProps {
  form: UseFormReturn<ContractFormValues>;
  selectedContractType: ContractType | null;
  setCurrencyModalOpen: (open: boolean) => void;
  servicesTotal?: number;
}

export function CostCurrencySection({ 
  form, 
  selectedContractType,
  setCurrencyModalOpen,
  servicesTotal
}: CostCurrencySectionProps) {
  const { t } = useTranslation();
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [conversionRate, setConversionRate] = useState<number>(1);
  const [isConverting, setIsConverting] = useState(false);
  const { canUseCurrencyConversion } = useFeatureAccess();
  
  const { useCurrenciesQuery, useCurrencyQuery } = useCurrencies();
  const { data: currencies = [] } = useCurrenciesQuery();
  
  const { useUserPreferencesQuery } = useUserPreferences();
  const { data: userPreferences } = useUserPreferencesQuery();

  useEffect(() => {
    const useServicesCost = form.watch("use_services_cost");
    
    if (useServicesCost && servicesTotal !== undefined) {
      form.setValue("expectedCost", servicesTotal, { shouldValidate: true });
    }
  }, [form.watch("use_services_cost"), servicesTotal, form]);
  
  useEffect(() => {
    if (form.watch("use_services_cost") && servicesTotal !== undefined) {
      form.setValue("expectedCost", servicesTotal, { shouldValidate: true });
    }
  }, [servicesTotal, form]);
  
  useEffect(() => {
    if (userPreferences?.defaultCurrencyId && !form.getValues("currencyId")) {
      form.setValue("currencyId", userPreferences.defaultCurrencyId);
    }
  }, [userPreferences, form]);
  
  const { 
    data: selectedCurrencyData
  } = useCurrencyQuery(form.watch("currencyId") || '');
  
  const { 
    data: defaultCurrencyData,
    isLoading: isDefaultCurrencyLoading
  } = useCurrencyQuery(userPreferences?.defaultCurrencyId || '');
  
  const isIncomingContract = selectedContractType?.direction === "incoming";
  const costLabel = isIncomingContract ? t('expectedIncome') : t('expectedCost');
  
  useEffect(() => {
    const convertCurrency = async () => {
      const expectedCost = form.watch("expectedCost");
      const currencyId = form.watch("currencyId");
      
      if (
        canUseCurrencyConversion && 
        selectedCurrencyData && 
        defaultCurrencyData && 
        selectedCurrencyData.id !== defaultCurrencyData.id &&
        currencyId && 
        userPreferences?.defaultCurrencyId &&
        expectedCost
      ) {
        setIsConverting(true);
        try {
          const result = await currencyConverter.convert(
            expectedCost,
            currencyId,
            userPreferences.defaultCurrencyId
          );
          
          setConvertedAmount(result.convertedAmount);
          setConversionRate(result.rate);
        } catch (error) {
          console.error("Error converting currency:", error);
          setConvertedAmount(null);
        } finally {
          setIsConverting(false);
        }
      } else {
        setConvertedAmount(null);
      }
    };
    
    convertCurrency();
  }, [form.watch("expectedCost"), form.watch("currencyId"), selectedCurrencyData, defaultCurrencyData, userPreferences?.defaultCurrencyId, canUseCurrencyConversion]);

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-end space-x-2">
        <FeatureGuard feature="contract_services">
          <FormField
            control={form.control}
            name="use_services_cost"
            render={({ field }) => (
              <div className="flex items-center space-x-2">
                <Switch
                  id="use-services-cost"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <Label htmlFor="use-services-cost" className="text-sm">
                  {t("Use services total")}
                </Label>
              </div>
            )}
          />
        </FeatureGuard>
      </div>
      
      <div className="grid gap-4 grid-cols-2">
        <FormField
          control={form.control}
          name="expectedCost"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{costLabel}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={isIncomingContract ? t("Enter expected income") : t("Enter expected cost")}
                  {...field}
                  onChange={(e) => field.onChange(parseFloat(e.target.value))}
                  disabled={form.watch("use_services_cost")}
                  className={form.watch("use_services_cost") ? "bg-muted cursor-not-allowed" : ""}
                />
              </FormControl>
              {form.watch("use_services_cost") && servicesTotal !== undefined && (
                <FormDescription className="text-xs text-blue-500">
                  {t("Using total from services")}
                </FormDescription>
              )}
              {convertedAmount !== null && defaultCurrencyData && canUseCurrencyConversion && (
                <FeatureGuard feature="currency_conversion">
                  <div className="mt-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 rounded-full cursor-help w-fit">
                            <RefreshCw className="h-3 w-3 text-blue-500" />
                            <span className="text-blue-700 dark:text-blue-300">
                              {convertedAmount.toLocaleString(undefined, {maximumFractionDigits: 2})} 
                              {defaultCurrencyData.symbol}
                            </span>
                            <AlertTriangle className="h-3 w-3 text-amber-500 ml-0.5 opacity-70" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="bg-black text-white border-gray-800 p-3 max-w-[300px]">
                          <div className="space-y-2">
                            <p>{t("Converted at rate")}: 1 {selectedCurrencyData?.code || ''} = {conversionRate.toFixed(4)} {defaultCurrencyData.code}</p>
                            <p className="text-amber-300 text-xs flex items-start gap-1.5">
                              <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span>{t("Currency conversions are estimates and may not reflect exact rates at the time of contract payment.")}</span>
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </FeatureGuard>
              )}
              {isConverting && canUseCurrencyConversion && (
                <div className="mt-2 text-xs text-gray-400">{t("Converting")}...</div>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="currencyId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Currency")}</FormLabel>
              {!canUseCurrencyConversion ? (
                <div>
                  <Input 
                    value={currencies.find(c => c.id === field.value)?.name || t("Loading...")}
                    disabled
                    className="bg-muted"
                  />
                  <div className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
                    <InfoIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      {t("Currency selection is only available with the currency conversion feature. Using your default currency.")}
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || userPreferences?.defaultCurrencyId || ""}
                    defaultValue={userPreferences?.defaultCurrencyId || ""}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("Select currency")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {currencies.map((currency) => (
                        <SelectItem key={currency.id} value={currency.id}>
                          {currency.code} - {currency.name} ({currency.symbol})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    <Button
                      type="button"
                      variant="link"
                      className="p-0 h-auto text-xs"
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrencyModalOpen(true);
                      }}
                    >
                      + {t("Add new currency")}
                    </Button>
                  </FormDescription>
                </>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
